import { errorIdentityOf } from "@pandas/logger";

export type ApiResponse<T> = {
	success: boolean;
	data: T;
	message: string;
	errorCode?: string;
};

export class NetworkError extends Error {
	constructor(message = "fetch failed") {
		super(message);
		this.name = "NetworkError";
	}
}

export type ApiErrorInit = {
	errorCode: string;
	/** The HTTP status the platform answered with; absent when the error was raised client-side. */
	status?: number;
	data?: Record<string, unknown>;
};

const stringField = (data: Record<string, unknown> | undefined, key: string): string | undefined => {
	const value = data?.[key];
	return typeof value === "string" && value.length > 0 ? value : undefined;
};

export class ApiError extends Error {
	public errorCode: string;
	/** 0 when the error was raised client-side (a config the app cannot use). */
	public status: number;
	public data?: Record<string, unknown>;
	/** The provider's own code and message, when the platform carried them in `data`. */
	public providerCode?: string;
	public providerMessage?: string;
	constructor(message: string, init: ApiErrorInit) {
		super(message);
		this.errorCode = init.errorCode;
		this.status = init.status ?? 0;
		this.data = init.data;
		this.providerCode = stringField(init.data, "providerCode");
		this.providerMessage = stringField(init.data, "providerMessage");
		this.name = "ApiError";
	}
}

export class AbortError extends Error {
	constructor(message = "Request aborted") {
		super(message);
		this.name = "AbortError";
	}
}

export const isAbortError = (error: unknown): boolean =>
	error instanceof AbortError || (error instanceof Error && error.name === "AbortError");

export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError;

/**
 * A failure `BaseHttpService` already logged above info: a NetworkError, or an
 * ApiError whose status drew warning or worse (400 and up). A client-side or
 * 2xx-body ApiError (status 0) logged only info, so its call site keeps its own
 * level — relocating severity onto an info line would hide the failure.
 */
export const isClientLoggedError = (error: unknown): error is ApiError | NetworkError =>
	error instanceof NetworkError || (error instanceof ApiError && error.status >= 400);

/**
 * Caught-error metadata for a call site's log line. Message prose rides only
 * through the logger's allowlist gate (`ApiError` is deliberately not in it, so
 * platform prose stays on the NETWORK line); codes and statuses are always
 * safe. Never the raw object and never `ApiError.data`.
 */
export const caughtErrorLogFields = (error: unknown): Record<string, string | number | undefined> => {
	const { errorName, errorMessage } = errorIdentityOf(error);
	if (error instanceof ApiError) {
		return { error_code: error.errorCode, error_message: errorMessage ?? undefined, status: error.status };
	}
	return { error_name: errorName, error_message: errorMessage ?? undefined };
};
